// frontend/src/components/report/ReputationTab.js
import { Star, TrendingUp, Flag, ExternalLink, MessageCircle, Copy } from "lucide-react";
import { toast } from "sonner";
import { ReportCard } from "./primitives/ReportCard";
import { StatTile } from "./primitives/StatTile";
import { FindingsList } from "./primitives/FindingsList";
import { REPUTATION_PILLARS, qualityTone, daysAgoLabel, PILLAR_MAX } from "./reportUtils";

function MiniBar({ value, max, color = "hsl(217,100%,59%)" }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="mt-1 h-1.5 w-full max-w-[120px] overflow-hidden rounded-full bg-secondary">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function ReputationTab({ data, lang, L }) {
  const ra = data.review_analysis;
  const reputation = data.reputation;
  const findings = data.audit_findings || [];
  const pillars = data.score_pillars || {};

  const themeMax = Math.max(
    1,
    ...(reputation?.positive_themes || []).map((t) => t.count),
    ...(reputation?.negative_themes || []).map((t) => t.count)
  );

  const copyDraftReply = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success(L.respondableCopied);
  };

  return (
    <>
      {(data.review_count != null || ra?.index != null || (reputation && !reputation.error)) && (
        <ReportCard icon={<Star className="h-4 w-4" />} title={L.reviewsTitle}>
          {/* Hero row: the two numbers everyone actually asks first — the
              real Google star average and the total count. Previously
              missing `data.rating` entirely while showing a differently-
              scaled "Bewertungsqualität X.X/5" tile right next to where a
              star rating would be — same "X.X/5" shape as a real rating,
              easy to mistake for one (user feedback 2026-09-14: this
              section reads as confusing). That index is now its own,
              distinctly-labeled box further down instead. */}
          <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
            {data.rating != null && (
              <div>
                <div className="flex items-center gap-1.5 text-3xl font-bold tabular-nums">
                  <Star className="h-6 w-6 fill-amber-400 text-amber-400" /> {data.rating}
                </div>
                <div className="text-sm text-muted-foreground">{L.averageRatingLabel}</div>
              </div>
            )}
            {data.review_count != null && <StatTile value={data.review_count} label={L.reviewsLabel} />}
          </div>

          {reputation && !reputation.error && (
            <div className="mt-4 flex flex-wrap gap-2 border-t pt-4">
              {reputation.last_90d != null && (
                <span className="rounded-full border bg-secondary/40 px-3 py-1 text-sm">
                  <strong className="tabular-nums">{reputation.last_90d}</strong> {L.newIn90Days}
                </span>
              )}
              {reputation.newest_review_days_ago != null && (
                <span className="rounded-full border bg-secondary/40 px-3 py-1 text-sm">
                  {L.newestReview}: {daysAgoLabel(reputation.newest_review_days_ago, lang)}
                </span>
              )}
              {reputation.response_rate_pct != null && (
                <span className="rounded-full border bg-secondary/40 px-3 py-1 text-sm">
                  {L.responseRate}: <strong className="tabular-nums">{reputation.response_rate_pct}%</strong>
                </span>
              )}
              {reputation.negative_pct != null && (
                <span className={`rounded-full border px-3 py-1 text-sm ${reputation.negative_pct > 15 ? "border-amber-200 bg-amber-50 text-amber-900" : "bg-secondary/40"}`}>
                  {L.negativeReviews}: <strong className="tabular-nums">{reputation.negative_pct}%</strong>
                </span>
              )}
            </div>
          )}

          {ra?.index != null && (
            <div className="mt-3 rounded-lg bg-secondary/50 p-3 text-sm">
              <span className="font-semibold">{L.showcaseQualityLabel}: <span className="tabular-nums">{ra.index}/100</span></span>
              <p className="mt-0.5 text-xs text-muted-foreground">{L.showcaseQualityNote}</p>
            </div>
          )}

          {data.review_count != null && !data.is_detailed && (
            <p className="mt-3 rounded-lg bg-secondary/60 p-3 text-sm text-muted-foreground">
              {L.reviewsTeaser(data.review_count, data.benchmark?.review_median, ra?.index)}
            </p>
          )}
        </ReportCard>
      )}

      <FindingsList
        findings={findings}
        pillars={pillars}
        pillarMax={PILLAR_MAX}
        pillarKeys={REPUTATION_PILLARS}
        pillarLabels={L.pillars}
        severityLabels={L.severity}
        headingText={L.foundHeading}
      />

      {ra?.index != null && (
        <div className="mt-6">
          <h2 className="mb-2 font-heading text-lg font-semibold">{L.showcaseTitle}</h2>
          <p className="mb-3 rounded-lg bg-secondary/60 p-3 text-sm text-muted-foreground">{L.showcaseDesc}</p>
          <div className="space-y-2">
            {(ra.items || []).map((it, i) => {
              const quality = it.score + 1;
              const qTone = qualityTone(quality);
              return (
                <div key={i} className="rounded-lg border bg-card p-3 text-base">
                  <div className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-0.5 tabular-nums"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {it.rating}</span>
                    <span>{it.hits.length ? it.hits.map((h) => L.hits[h] || h).join(" · ") : L.noSpecificDetails}</span>
                    <span className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-sm font-semibold" style={{ background: qTone.bg, color: qTone.fg }} title={L.strengthTooltip}>
                      {L.strengthLabel(quality)}
                    </span>
                  </div>
                  <p className="text-muted-foreground">{it.excerpt}…</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {reputation && !reputation.error && (reputation.response_quality || reputation.positive_themes?.length > 0 || reputation.negative_themes?.length > 0 || reputation.appealable_reviews?.length > 0 || reputation.appealable_examined_count > 0 || reputation.respondable_reviews?.length > 0 || reputation.competitors?.length > 0) && (
        <ReportCard variant="accent" icon={<TrendingUp className="h-4 w-4" />} title={L.reputationTitle} description={L.reputationDesc(reputation.sample_size, reputation.total_review_count)}>
          {reputation.response_quality && (
            <p className="text-base text-muted-foreground">
              {L.responseQualityIntro}{" "}
              <strong>{reputation.response_quality === "individuell" ? L.responseQuality.individuell : reputation.response_quality === "standardisiert" ? L.responseQuality.standardisiert : L.responseQuality.none}</strong>.
            </p>
          )}

          {(reputation.positive_themes?.length > 0 || reputation.negative_themes?.length > 0) && (
            <div className="grid gap-4 sm:grid-cols-2">
              {reputation.positive_themes?.length > 0 && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{L.positiveThemes}</h3>
                  <ul className="space-y-1.5">
                    {reputation.positive_themes.map((t, i) => (
                      <li key={i} className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-base text-green-900">
                        <div className="flex items-center justify-between"><span>{t.theme}</span><span className="text-sm font-semibold tabular-nums">{t.count}×</span></div>
                        <MiniBar value={t.count} max={themeMax} color="hsl(142,55%,42%)" />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {reputation.negative_themes?.length > 0 && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{L.negativeThemes}</h3>
                  <ul className="space-y-1.5">
                    {reputation.negative_themes.map((t, i) => (
                      <li key={i} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-base text-amber-900">
                        <div className="flex items-center justify-between"><span>{t.theme}</span><span className="text-sm font-semibold tabular-nums">{t.count}×</span></div>
                        <MiniBar value={t.count} max={themeMax} color="hsl(38,90%,48%)" />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {reputation.appealable_reviews?.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50/60 p-4 sm:p-5">
              <h3 className="flex items-center gap-1.5 text-base font-semibold text-red-900"><Flag className="h-4 w-4" /> {L.appealableTitle}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-red-800/80">{L.appealableDesc}</p>
              <ul className="mt-3 space-y-2.5">
                {reputation.appealable_reviews.map((r, i) => (
                  <li key={i} className="rounded-lg border border-red-200 bg-white p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-sm font-semibold text-red-900">{L.appealableCategory[r.category] || r.category}</span>
                      {r.rating != null && <span className="flex items-center gap-0.5 text-sm font-semibold text-muted-foreground">{r.rating} <Star className="h-3 w-3 fill-current" /></span>}
                      {r.profile_name && <span className="text-sm text-muted-foreground">{r.profile_name}</span>}
                    </div>
                    {r.review_text && <p className="mt-1.5 text-base text-foreground/90">“{r.review_text}”</p>}
                    <p className="mt-1.5 text-sm text-muted-foreground">{r.reasoning}</p>
                    {r.review_url && (
                      <a href={r.review_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                        {L.appealableViewOnGoogle} <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {reputation.appealable_reviews?.length === 0 && reputation.appealable_examined_count > 0 && (
            <div className="flex items-start gap-2 rounded-xl border border-green-200 bg-green-50/60 p-4 text-sm text-green-900 sm:p-5">
              <Flag className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{L.appealableNoneFound(reputation.appealable_examined_count)}</span>
            </div>
          )}

          {reputation.respondable_reviews?.length > 0 && (
            <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 sm:p-5">
              <h3 className="flex items-center gap-1.5 text-base font-semibold text-blue-900"><MessageCircle className="h-4 w-4" /> {L.respondableTitle}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-blue-800/80">{L.respondableDesc}</p>
              <ul className="mt-3 space-y-2.5">
                {reputation.respondable_reviews.map((r, i) => (
                  <li key={i} className="rounded-lg border border-blue-200 bg-white p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {r.rating != null && <span className="flex items-center gap-0.5 text-sm font-semibold text-muted-foreground">{r.rating} <Star className="h-3 w-3 fill-current" /></span>}
                      {r.profile_name && <span className="text-sm text-muted-foreground">{r.profile_name}</span>}
                    </div>
                    {r.review_text && <p className="mt-1.5 text-base text-foreground/90">“{r.review_text}”</p>}
                    {r.draft_reply && (
                      <div className="mt-2.5 rounded-lg border border-blue-100 bg-blue-50 p-3">
                        <p className="text-sm text-blue-900">{r.draft_reply}</p>
                        <button
                          type="button"
                          onClick={() => copyDraftReply(r.draft_reply)}
                          className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                        >
                          <Copy className="h-3 w-3" /> {L.respondableCopy}
                        </button>
                      </div>
                    )}
                    {r.review_url && (
                      <a href={r.review_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                        {L.respondableViewOnGoogle} <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {reputation.competitors?.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{L.competitorTableTitle}</h3>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-base">
                  <thead>
                    <tr className="border-b bg-secondary/50 text-left text-sm text-muted-foreground">
                      <th className="p-2.5 font-medium">{L.company}</th>
                      <th className="p-2.5 font-medium text-right">{L.rating}</th>
                      <th className="p-2.5 font-medium text-right">{L.reviewsLabel}</th>
                      <th className="p-2.5 font-medium text-right">{L.lastReview}</th>
                      <th className="p-2.5 font-medium">{L.topNegativeTheme}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-accent/30 font-semibold">
                      <td className="p-2.5">{L.youSuffix(data.name)}</td>
                      <td className="p-2.5 text-right tabular-nums">{data.rating ?? "–"}</td>
                      <td className="p-2.5 text-right tabular-nums">{reputation.total_review_count ?? data.review_count ?? "–"}</td>
                      <td className="p-2.5 text-right tabular-nums">{daysAgoLabel(reputation.newest_review_days_ago, lang)}</td>
                      <td className="p-2.5">{reputation.negative_themes?.[0]?.theme || "–"}</td>
                    </tr>
                    {reputation.competitors.map((c, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="max-w-[180px] truncate p-2.5" title={c.name}>{c.name}</td>
                        <td className="p-2.5 text-right tabular-nums">{c.rating ?? "–"}</td>
                        <td className="p-2.5 text-right tabular-nums">{c.review_count ?? "–"}</td>
                        <td className="p-2.5 text-right tabular-nums">{daysAgoLabel(c.newest_review_days_ago, lang)}</td>
                        <td className="p-2.5">{c.top_negative_theme || "–"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </ReportCard>
      )}

      {reputation && !reputation.error && (
        <ReportCard icon={<MessageCircle className="h-4 w-4" />} title={L.reviewTipsTitle}>
          <div>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{L.reviewTipsRequestTitle}</h3>
            <div className="space-y-2.5">
              <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                <p className="text-sm font-semibold text-green-900">{L.reviewTipsWhatsapp}</p>
                <p className="mt-1 text-base text-green-900/90">{L.reviewTipsWhatsappText(data.name)}</p>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm font-semibold text-blue-900">{L.reviewTipsEmail}</p>
                <p className="mt-1 text-base text-blue-900/90">{L.reviewTipsEmailText}</p>
              </div>
            </div>
          </div>

          <div className="mt-5 border-t pt-4">
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{L.reviewTipsNegativeTitle}</h3>
            <ol className="space-y-2">
              <li className="rounded-lg border p-3 text-base"><strong>{L.reviewTipsNegativeStep1}</strong> {L.reviewTipsNegativeStep1Text}</li>
              <li className="rounded-lg border p-3 text-base"><strong>{L.reviewTipsNegativeStep2}</strong> {L.reviewTipsNegativeStep2Text}</li>
              <li className="rounded-lg border p-3 text-base"><strong>{L.reviewTipsNegativeStep3}</strong> {L.reviewTipsNegativeStep3Text}</li>
            </ol>
            <p className="mt-3 text-sm text-muted-foreground">{L.reviewTipsFooter}</p>
          </div>
        </ReportCard>
      )}

      {data.review_count == null && ra?.index == null && !(reputation && !reputation.error) && findings.filter((f) => REPUTATION_PILLARS.includes(f.pillar)).length === 0 && (
        <div className="rounded-2xl border bg-card p-5 text-base text-muted-foreground shadow-sm sm:p-6">{L.noDataYet}</div>
      )}
    </>
  );
}
